import type { MapPathDef } from './types';

export const MAP_PATH_DEFS: Array<MapPathDef> = [
  { path: 'participant.id', type: 'string' },
  { path: 'participant.name', type: 'string' },
  { path: 'participant.startNumber', type: 'string' },
  { path: 'participant.tags', type: 'object' },
  { path: 'participant.markerColor', type: 'string' },
  { path: 'participant.importId', type: 'string' },
  { path: 'current.time', type: 'string' },
  { path: 'current.lat', type: 'number', hidden: true },
  { path: 'current.lng', type: 'number', hidden: true },
  { path: 'current.elv', type: 'number' },
  { path: 'current.speed', type: 'number' },
  { path: 'current.isRef', type: 'boolean' },
  { path: 'current.latSt', type: 'number', hidden: true },
  { path: 'current.lngSt', type: 'number', hidden: true },
  { path: 'current.distanceToShadowTrack', type: 'number' },
  { path: 'current.speedSt', type: 'number', defaultVisible: true },
  { path: 'current.eta', type: 'number' },
  { path: 'current.fromStart', type: 'number', defaultVisible: true },
  { path: 'current.toFinish', type: 'number' },
  { path: 'current.gapCalcTime', type: 'string' },
  { path: 'current.gapDistance', type: 'number' },
  { path: 'current.gap', type: 'number' },
  { path: 'current.gapChase', type: 'number' },
  { path: 'current.gapAverage', type: 'number' },
  { path: 'current.fromStartEx', type: 'number', defaultVisible: true },
  { path: 'current.toFinishEx', type: 'number' },
  { path: 'current.latStEx', type: 'number', hidden: true },
  { path: 'current.lngStEx', type: 'number', hidden: true },
  { path: 'current.gapDistanceEx', type: 'number', defaultVisible: true },
  { path: 'current.gapEx', type: 'number' },
  { path: 'current.gapChaseEx', type: 'number' },
  { path: 'current.gapAverageEx', type: 'number' },
  { path: 'device.externalId', type: 'string' },
  { path: 'device.battery', type: 'number' },
  { path: 'device.online', type: 'boolean' },
  { path: 'device.lastLocationGpsFix', type: 'boolean' },
  { path: 'device.lastLocationAt', type: 'string' },
  { path: 'device.deviceId', type: 'string' },
  { path: 'device.isMoving', type: 'boolean' },
];

// ─── Shared gap color palette ─────────────────────────────────────────────────
// Exported so the over-time chart can use the same colors for its series.
// Groups: (gap / gapEx) purple, (gapChase / gapChaseEx) teal, (gapAverage / gapAverageEx) amber,
//         (gapDistance / gapDistanceEx) pink.  Ex variants are lighter shades of the same hue (better contrast on dark bg).
export const CurrentAPIColorTable = {
  helperLine: '#BDBDBD', // for gap distance helper lines in GapsChart
  // ── Gap metric colors ──────────────────────────────────────────────────────
  gapDistance: '#EC407A',
  gapDistanceEx: '#F48FB1',
  gap: '#AB47BC',
  gapEx: '#CE93D8',
  gapChase: '#26A69A',
  gapChaseEx: '#80CBC4',
  gapAverage: '#FFCA28',
  gapAverageEx: '#F57F17',
  // ── Participant (selected starter) — blue family ───────────────────────────
  participant: '#42A5F5', // raw GPS dots, speed line, chart series
  participantStroke: '#0D47A1', // current-position dot in GapsChart (darkest blue)
  participantHover: '#1E88E5', // hover: GPS coords (lat / lng)
  participantHoverFill: '#90CAF9',
  participantHoverSt: '#0D47A1', // hover: shadowtrack (latSt / lngSt)
  participantHoverStFill: '#1565C0',
  shadowTrack: '#36739a',
  shadowTrackCorridor: '#36739a',
  shadowTrackCorridorFill: '#5B8DB830', // very faint blueish fill for the mapping distance corridor
  // ── Leader / Reference — orange family ────────────────────────────────────
  leader: '#FFA726', // raw GPS dots, speed line, chart series
  leaderStroke: '#E65100', // current-position dot in GapsChart (darkest orange)
  leaderHover: '#FB8C00', // hover: GPS coords
  leaderHoverFill: '#FFCC80',
  leaderHoverSt: '#E65100', // hover: shadowtrack
  leaderHoverStFill: '#FF8A65',
} as const;

const CurrentAPIColorTableLight: Record<keyof typeof CurrentAPIColorTable, string> = {
  ...CurrentAPIColorTable,
  helperLine: '#757575',
};

export function currentAPIColors(
  isDayMode: boolean,
): Record<keyof typeof CurrentAPIColorTable, string> {
  return isDayMode ? CurrentAPIColorTableLight : CurrentAPIColorTable;
}
